























import { Component, Vue, Prop } from 'vue-property-decorator'
import BaseImage from 'vuecomponents/base_image.vue'
import { proxiedUrl, processedUrl } from '@padlet/vivaldi-client'

/**
 * Thumbnail that renders a colored background while image is loading.
 * @displayName Image Thumbnail
 */
@Component({
  components: { BaseImage },
})
export default class ImageThumbnail extends Vue {
  loaded = false
  error = false
  /**
   * Background color for the thumbnail.
   * @values red, blue, green, yellow, purple, pink, cyan, orange
   */
  @Prop({ default: 'green' }) backgroundColor!: string
  /**
   * Aspect ratio for the image in case cropping is desired
   */
  @Prop({ default: undefined }) cropAspectRatio!: number
  /**
   * Original image width
   */
  @Prop({ required: true }) thumbnailWidth!: number
  /**
   * Original image height
   */
  @Prop({ required: true }) thumbnailHeight!: number
  /**
   * Determines whether image should be proxied to vivaldi for caching
   */
  @Prop({ default: false }) proxyImage!: boolean
  /**
   * Determines whether image should be processed before loading
   */
  @Prop({ default: false }) processImage!: boolean
  /**
   * Title to display when the image fails to load
   */
  @Prop({ default: '' }) title!: string
  /**
   * URL for image
   */
  @Prop({ required: true }) src!: string
  /**
   * Resulting component width
   */
  @Prop({ required: true }) width!: number
  /**
   * Aspect ratio to stretch the image to
   */
  @Prop({ default: undefined }) forceAspectRatio!: number

  @Prop({ default: true }) lazyLoading!: boolean

  get bgColorClass(): string {
    if (this.loaded) {
      return ''
    }
    switch (this.backgroundColor) {
      case 'red':
        return 'bg-bgi-red'
      case 'blue':
        return 'bg-bgi-blue'
      case 'green':
        return 'bg-bgi-green'
      case 'yellow':
        return 'bg-bgi-yellow'
      case 'purple':
        return 'bg-bgi-purple'
      case 'pink':
        return 'bg-bgi-pink'
      case 'cyan':
        return 'bg-bgi-cyan'
      case 'orange':
        return 'bg-bgi-orange'
      case 'grey':
      default:
        return 'bg-highway-200'
    }
  }
  get url(): string {
    if (this.processImage) {
      const processingOptions: { width: number; ar?: number } = { width: this.width }
      if (this.forceAspectRatio) {
        processingOptions.ar = this.forceAspectRatio
      }
      return processedUrl(this.src, processingOptions)
    }
    return this.proxyImage ? proxiedUrl(this.src) : this.src
  }
  get detectedAspectRatio(): number {
    return this.thumbnailWidth / this.thumbnailHeight
  }
  get effectiveAspectRatio(): number {
    return this.cropAspectRatio || this.forceAspectRatio || this.detectedAspectRatio
  }
  get imageWidth(): number {
    if (this.cropAspectRatio) return this.width * Math.max(1, this.effectiveAspectRatio / this.cropAspectRatio)
    return this.width
  }
  get imageHeight(): number {
    return Math.max(this.imageWidth / this.effectiveAspectRatio, 1)
  }
  get height(): number {
    return Math.max(this.width / this.effectiveAspectRatio, 1)
  }
  onLoad(): void {
    this.loaded = true
    this.$emit('load')
  }
  onError(): void {
    this.error = true
    this.$emit('error')
  }
}
